<template>
  <div class="active-user-stats">
    <div class="page-header">
      <h2>活跃度统计</h2>
      <div class="header-controls">
        <div class="date-search">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
          />
          <el-button type="primary" :icon="Search" @click="handleSearch">搜索</el-button>
        </div>
      </div>
    </div>

    <!-- 统计说明 -->
    <div class="stats-description">
      <el-alert
        title="统计说明"
        type="info"
        :closable="false"
        show-icon
      >
        <template #default>
          <div class="description-content">
            <p><strong>软件活跃用户</strong>：使用C#软件进行影像处理的用户</p>
            <p><strong>网站访客</strong>：浏览网站页面的用户（包括游客和已登录用户）</p>
          </div>
        </template>
      </el-alert>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="4">
          <el-card class="stat-card software-card">
            <template #header>
              <div class="card-header">
                <span>累计使用量</span>
                <el-icon><Monitor /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ softwareStats.totalVisits }}</div>
              <div class="stat-label">该时间段累计软件使用次数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="4">
          <el-card class="stat-card software-card">
            <template #header>
              <div class="card-header">
                <span>选定日期使用量</span>
                <el-icon><DataLine /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ softwareStats.periodVisits }}</div>
              <div class="stat-label">选定时间段软件访问量</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="4">
          <el-card class="stat-card software-card">
            <template #header>
              <div class="card-header">
                <span>今日使用量</span>
                <el-icon><Monitor /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ softwareStats.todayVisits }}</div>
              <div class="stat-label">今日软件访问次数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="4">
          <el-card class="stat-card website-card">
            <template #header>
              <div class="card-header">
                <span>总访问量</span>
                <el-icon><View /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ websiteStats.totalVisits }}</div>
              <div class="stat-label">累计网站访问次数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="4">
          <el-card class="stat-card website-card">
            <template #header>
              <div class="card-header">
                <span>选定日期访问量</span>
                <el-icon><View /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ websiteStats.periodTotalVisits }}</div>
              <div class="stat-label">选定时间段网站访问量</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="4">
          <el-card class="stat-card website-card">
            <template #header>
              <div class="card-header">
                <span>今日访问量</span>
                <el-icon><View /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ websiteStats.todayVisits }}</div>
              <div class="stat-label">今日网站访问次数</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 软件活跃统计模块 -->
    <div class="module-section">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="module-card software-module">
            <template #header>
              <div class="module-header">
                <div class="module-title">
                  <el-icon><Monitor /></el-icon>
                  <span>选定日期软件累计使用量</span>
                </div>
                <div class="module-stats">
                  <el-tag type="primary">累计软件使用量: {{ softwareStats.totalVisits }}</el-tag>
                </div>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="allSoftwareChartData"
                :chart-options="allSoftwareChartOptions"
                height="300px"
              />
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="module-card software-module">
            <template #header>
              <div class="module-header">
                <div class="module-title">
                  <el-icon><Monitor /></el-icon>
                  <span>选定日期每日软件使用量</span>
                </div>
                <div class="module-stats">
                  <el-tag type="success">该时间段软件访问量: {{ softwareStats.periodVisits }}</el-tag>
                  <el-tag type="warning">该时间段软件访问率: {{ softwareStats.periodActiveRate }}%</el-tag>
                </div>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="periodSoftwareChartData"
                :chart-options="periodSoftwareChartOptions"
                height="300px"
              />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 网站访客统计模块 -->
    <div class="module-section">
      <el-row :gutter="20">
        
        <el-col :span="12">
          <el-card class="module-card website-module">
            <template #header>
              <div class="module-header">
                <div class="module-title">
                  <el-icon><View /></el-icon>
                  <span>选定日期网站累计访问量</span>
                </div>
                <div class="module-stats">
                  <el-tag type="primary">该时间段总访问量: {{ websiteStats.periodTotalVisits }}</el-tag>
                </div>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="totalVisitChartData"
                :chart-options="totalVisitChartOptions"
                height="300px"
              />
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="module-card website-module">
            <template #header>
              <div class="module-header">
                <div class="module-title">
                  <el-icon><UserFilled /></el-icon>
                  <span>选定日期网站每日访问量</span>
                </div>
                <div class="module-stats">
                  <el-tag type="success">该时间段游客访问量: {{ websiteStats.periodVisitorVisits }}</el-tag>
                </div>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="visitorChartData"
                :chart-options="visitorChartOptions"
                height="300px"
              />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 地区统计 -->
    <div class="region-stats">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="region-card">
            <template #header>
              <div class="card-header">
                <span>🌍 网站访问省份统计</span>
                <el-tag type="warning">TOP 10</el-tag>
              </div>
            </template>
            <div class="region-list">
              <div 
                v-for="(item, index) in regionStats" 
                :key="item.region"
                class="region-item"
              >
                <div class="region-rank">{{ index + 1 }}</div>
                <div class="region-info">
                  <div class="region-name">{{ item.region || '未知' }}</div>
                  <div class="region-details">
                    <span class="visitor-count">{{ item.unique_visitors }} 访客</span>
                    <span class="visit-count">{{ item.total_visits }} 访问</span>
                  </div>
                </div>
                <div class="region-bar">
                  <div 
                    class="bar-fill" 
                    :style="{ width: getBarWidth(item.unique_visitors) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="region-card">
            <template #header>
              <div class="card-header">
                <span>🏙️ 网站访问城市统计</span>
                <el-tag type="warning">TOP 10</el-tag>
              </div>
            </template>
            <div class="region-list">
              <div 
                v-for="(item, index) in cityStats" 
                :key="item.city"
                class="region-item"
              >
                <div class="region-rank">{{ index + 1 }}</div>
                <div class="region-info">
                  <div class="region-name">{{ item.city || '未知' }}</div>
                  <div class="region-details">
                    <span class="visitor-count">{{ item.unique_visitors }} 访客</span>
                    <span class="visit-count">{{ item.total_visits }} 访问</span>
                  </div>
                </div>
                <div class="region-bar">
                  <div 
                    class="bar-fill" 
                    :style="{ width: getBarWidth(item.unique_visitors) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import ChartBox from '@/components/admin/ChartBox.vue'
import { User, UserFilled, DataLine, Search, View, Monitor } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 日期范围选择
const dateRange = ref([])
const dateShortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 统计数据
const stats = ref({
  averageActiveUsers: 0,
  maxActiveUsers: 0,
  activeRate: 0,
  totalVisits: 0,
  totalUniqueVisitors: 0,
  averageVisits: 0
})

// 软件活跃统计
const softwareStats = ref({
  totalActiveUsers: 0,
  periodActiveUsers: 0,
  periodActiveRate: 0,
  totalVisits: 0,
  periodVisits: 0,
  todayVisits: 0
})

// 网站访客统计
const websiteStats = ref({
  totalVisits: 0,
  periodVisitorVisits: 0,
  periodUserVisits: 0,
  periodTotalVisits: 0,
  todayVisits: 0
})

// 地区统计
const regionStats = ref([])
const cityStats = ref([])

// 软件活跃图表数据
const allSoftwareChartData = ref({
  labels: [],
  datasets: [
    {
      label: '软件活跃用户',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const allSoftwareChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

const periodSoftwareChartData = ref({
  labels: [],
  datasets: [
    {
      label: '软件活跃用户',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const periodSoftwareChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

// 网站访客图表数据
const visitorChartData = ref({
  labels: [],
  datasets: [
    {
      label: '游客访问量',
      data: [],
      backgroundColor: 'rgba(103, 194, 58, 0.2)',
      borderColor: 'rgba(103, 194, 58, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const visitorChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

const totalVisitChartData = ref({
  labels: [],
  datasets: [
    {
      label: '总访问量',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const totalVisitChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

// 计算条形图宽度
const getBarWidth = (value) => {
  const maxValue = Math.max(
    ...regionStats.value.map(item => item.unique_visitors),
    ...cityStats.value.map(item => item.unique_visitors)
  )
  return maxValue > 0 ? (value / maxValue) * 100 : 0
}

// 更新软件活跃图表数据
const updateSoftwareChartData = (trendData, allSoftwareTrendData = []) => {
  if (!trendData || trendData.length === 0) {
    allSoftwareChartData.value = {
      labels: [],
      datasets: [
        {
          label: '软件访问量',
          data: [],
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    periodSoftwareChartData.value = {
      labels: [],
      datasets: [
        {
          label: '软件访问量',
          data: [],
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    return
  }

  // 格式化日期标签
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // 创建日期映射
  const dateMap = new Map()
  trendData.forEach(item => {
    const date = new Date(item.date)
    const dateStr = date.toISOString().split('T')[0]
    dateMap.set(dateStr, item)
  })

  // 生成所有日期的数据（不填充空白日期）
  const allTrend = allSoftwareTrendData.length > 0 ? allSoftwareTrendData : trendData.map(item => ({
    date: item.date,
    software_visits: item.software_visits
  }))

  // 生成所选时间段的数据（填充空白日期）
  const filledTrend = []
  if (dateRange.value && dateRange.value.length === 2) {
    const start = new Date(dateRange.value[0])
    const end = new Date(dateRange.value[1])
    
    // 生成每一天的数据
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0]
      const data = dateMap.get(dateStr) || {}
      filledTrend.push({
        date: dateStr,
        software_visits: data.software_visits || 0
      })
    }
  } else {
    filledTrend.push(...trendData.map(item => ({
      date: item.date,
      software_visits: item.software_visits
    })))
  }

  // 生成累计软件使用量数据（填充空白日期，保持累加）
  const filledCumulativeTrend = []
  if (dateRange.value && dateRange.value.length === 2) {
    const start = new Date(dateRange.value[0])
    const end = new Date(dateRange.value[1])
    
    // 创建累计数据映射
    const cumulativeMap = new Map()
    allSoftwareTrendData.forEach(item => {
      const date = new Date(item.date)
      const dateStr = date.toISOString().split('T')[0]
      cumulativeMap.set(dateStr, item.cumulative_software_visits)
    })
    
    // 生成每一天的累计数据，保持累加效果
    let lastCumulativeValue = 0
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0]
      const cumulativeValue = cumulativeMap.get(dateStr)
      
      // 如果有累计值，使用它；否则保持上一个累计值
      if (cumulativeValue !== undefined) {
        lastCumulativeValue = cumulativeValue
      }
      
      filledCumulativeTrend.push({
        date: dateStr,
        cumulative_software_visits: lastCumulativeValue
      })
    }
  } else {
    filledCumulativeTrend.push(...allSoftwareTrendData.map(item => ({
      date: item.date,
      cumulative_software_visits: item.cumulative_software_visits
    })))
  }

  // 更新所有日期软件访问趋势图表（改为累计软件使用量）
  allSoftwareChartData.value = {
    labels: filledCumulativeTrend.map(item => formatDate(item.date)),
    datasets: [
      {
        label: '累计软件使用量',
        data: filledCumulativeTrend.map(item => item.cumulative_software_visits),
        backgroundColor: 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }

  // 更新所选时间段软件访问趋势图表
  periodSoftwareChartData.value = {
    labels: filledTrend.map(item => formatDate(item.date)),
    datasets: [
      {
        label: '软件访问量',
        data: filledTrend.map(item => item.software_visits),
        backgroundColor: 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }
}

// 更新网站访客图表数据
const updateWebsiteChartData = (trendData) => {
  if (!trendData || trendData.length === 0) {
    visitorChartData.value = {
      labels: [],
      datasets: [
        { label: '游客访问量', data: [], backgroundColor: 'rgba(103, 194, 58, 0.2)', borderColor: 'rgba(103, 194, 58, 1)', borderWidth: 2, tension: 0.4, fill: true }
      ]
    }
    totalVisitChartData.value = {
      labels: [],
      datasets: [
        { label: '总访问量', data: [], backgroundColor: 'rgba(64, 158, 255, 0.2)', borderColor: 'rgba(64, 158, 255, 1)', borderWidth: 2, tension: 0.4, fill: true }
      ]
    }
    return
  }

  // 格式化日期标签
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // 创建日期映射
  const dateMap = new Map()
  trendData.forEach(item => {
    const date = new Date(item.date)
    const dateStr = date.toISOString().split('T')[0]
    dateMap.set(dateStr, item)
  })

  // 生成完整的时间段数据
  const filledTrend = []
  if (dateRange.value && dateRange.value.length === 2) {
    const start = new Date(dateRange.value[0])
    const end = new Date(dateRange.value[1])
    
    // 生成每一天的数据
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0]
      const data = dateMap.get(dateStr) || {}
      filledTrend.push({
        date: dateStr,
        unique_visitors: data.unique_visitors || 0,
        logged_users: data.logged_users || 0,
        total_visits: data.total_visits || 0,
        guest_visits: data.guest_visits || 0
      })
    }
  } else {
    filledTrend.push(...trendData.map(item => ({
      date: item.date,
      unique_visitors: item.unique_visitors,
      logged_users: item.logged_users,
      total_visits: item.total_visits,
      guest_visits: item.guest_visits
    })))
  }

  visitorChartData.value = {
    labels: filledTrend.map(item => formatDate(item.date)),
    datasets: [
      {
        label: '游客访问量',
        data: filledTrend.map(item => item.guest_visits),
        backgroundColor: 'rgba(103, 194, 58, 0.2)',
        borderColor: 'rgba(103, 194, 58, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }

  totalVisitChartData.value = {
    labels: filledTrend.map(item => formatDate(item.date)),
    datasets: [
      {
        label: '总访问量',
        data: filledTrend.map(item => item.total_visits),
        backgroundColor: 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }

  // 更新网站访客统计模块的标签
  websiteStats.value.periodVisitorVisits = filledTrend.reduce((sum, item) => sum + item.guest_visits, 0)
  websiteStats.value.periodTotalVisits = filledTrend.reduce((sum, item) => sum + item.total_visits, 0)
}

// 获取统计数据
const fetchStats = async () => {
  try {
    console.log('开始获取活跃用户数据...')
    console.log('日期范围:', dateRange.value)
    console.log('开始日期:', dateRange.value[0]?.toISOString().split('T')[0])
    console.log('结束日期:', dateRange.value[1]?.toISOString().split('T')[0])
    
    const response = await request.get('/admin/stats/active', {
      params: {
        startDate: dateRange.value[0]?.toISOString().split('T')[0],
        endDate: dateRange.value[1]?.toISOString().split('T')[0]
      }
    })
    console.log('获取到的数据：', response)
    console.log('trend数据详情：', response.trend)
    console.log('allSoftwareTrend数据详情：', response.allSoftwareTrend)
    
    if (!response || !response.trend) {
      throw new Error('返回数据格式不正确')
    }
    
    // 更新统计数据
    stats.value = {
      averageActiveUsers: response.stats.averageActiveUsers,
      maxActiveUsers: response.stats.maxActiveUsers,
      activeRate: response.stats.activeRate,
      totalVisits: response.stats.totalVisits,
      totalUniqueVisitors: response.stats.totalUniqueVisitors,
      averageVisits: response.stats.averageVisits
    }
    
    // 更新地区统计
    regionStats.value = response.regionStats || []
    cityStats.value = response.cityStats || []
    
    // 计算软件活跃统计
    const trendData = response.trend.map(item => ({
      date: item.date,
      active_users: parseInt(item.active_users) || 0,
      software_visits: parseInt(item.software_visits) || 0,
      logged_users: parseInt(item.logged_users) || 0,
      total_visits: parseInt(item.total_visits) || 0,
      guest_visits: parseInt(item.guest_visits) || 0
    }))
    
    // 处理所有日期的软件访问量数据
    const allSoftwareTrendData = (response.allSoftwareTrend || []).map(item => ({
      date: item.date,
      software_visits: parseInt(item.software_visits) || 0,
      cumulative_software_visits: parseInt(item.cumulative_software_visits) || 0
    }))
    
    console.log('所有日期软件访问量数据:', allSoftwareTrendData);
    
    // 计算软件活跃统计（基于选定时间段）
    const totalActiveUsers = trendData.reduce((sum, item) => sum + item.active_users, 0)
    const periodActiveUsers = trendData.reduce((sum, item) => sum + item.active_users, 0)
    const periodActiveRate = response.stats.activeRate || 0
    
    // 计算累计软件使用量
    const cumulativeSoftwareVisits = allSoftwareTrendData.length > 0 
      ? allSoftwareTrendData[allSoftwareTrendData.length - 1]?.cumulative_software_visits || 0
      : 0
    
    softwareStats.value = {
      totalActiveUsers: totalActiveUsers,
      periodActiveUsers: periodActiveUsers,
      periodActiveRate: periodActiveRate,
      totalVisits: cumulativeSoftwareVisits, // 使用累计值
      periodVisits: response.stats.softwarePeriodVisits || 0,
      todayVisits: response.stats.softwareTodayVisits || 0
    }
    
    console.log('软件统计数据:', softwareStats.value);
    console.log('后端返回的软件统计数据:', {
      softwareTotalVisits: response.stats.softwareTotalVisits,
      softwarePeriodVisits: response.stats.softwarePeriodVisits,
      softwareTodayVisits: response.stats.softwareTodayVisits
    });
    
    // 计算网站访客统计（基于选定时间段）
    const periodVisitorVisits = trendData.reduce((sum, item) => sum + item.guest_visits, 0)
    const periodTotalVisits = trendData.reduce((sum, item) => sum + item.total_visits, 0)
    
    websiteStats.value = {
      totalVisits: response.stats.totalVisits || 0,
      periodVisitorVisits: periodVisitorVisits,
      periodUserVisits: trendData.reduce((sum, item) => sum + item.logged_users, 0),
      periodTotalVisits: periodTotalVisits,
      todayVisits: response.stats.websiteTodayVisits || 0
    }
    
    // 更新各个图表数据
    updateSoftwareChartData(trendData, allSoftwareTrendData)
    updateWebsiteChartData(trendData)
    
    console.log('统计数据更新完成：', stats.value)
  } catch (error) {
    console.error('获取统计数据失败：', error)
    ElMessage.error(error.message || '获取统计数据失败')
  }
}

// 搜索处理
const handleSearch = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期范围')
    return
  }
  fetchStats()
}

// 初始化
onMounted(() => {
  // 设置默认日期范围为最近一个月
  dateRange.value = dateShortcuts[1].value()
  // 初始化时加载数据
  fetchStats()
})
</script>

<style scoped>
.active-user-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}

.date-search {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stats-description {
  margin-bottom: 20px;
}

.description-content p {
  margin: 4px 0;
  font-size: 14px;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.software-card {
  border-left: 4px solid #409EFF;
}

.website-card {
  border-left: 4px solid #67C23A;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
  padding: 20px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

/* 模块样式 */
.module-section {
  margin-bottom: 20px;
}

.module-card {
  height: 100%;
}

.software-module {
  border-left: 4px solid #409EFF;
}

.website-module {
  border-left: 4px solid #67C23A;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.module-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
}

.module-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.region-stats {
  margin-bottom: 20px;
}

.region-card {
  height: 100%;
}

.region-list {
  max-height: 400px;
  overflow-y: auto;
}

.region-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.region-item:last-child {
  border-bottom: none;
}

.region-rank {
  width: 30px;
  height: 30px;
  background: #409EFF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  margin-right: 12px;
}

.region-info {
  flex: 1;
  margin-right: 12px;
}

.region-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.region-details {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.visitor-count {
  color: #67C23A;
}

.visit-count {
  color: #E6A23C;
}

.region-bar {
  width: 100px;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #409EFF, #67C23A);
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .module-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .module-stats {
    justify-content: flex-start;
  }
}
</style> 